import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

class ArticleHero {
  private hero: HTMLElement;
  private container: HTMLElement;
  private mouseBlob: HTMLElement;

  private blueSVG: SVGElement;
  private yellowSVG: SVGElement;
  private redSVG: SVGElement;
  private mouseX: number = window.innerWidth / 2;
  private mouseY: number = 0;
  private rafId: number | null = null;
  private heading: HTMLElement | null = null;

  constructor(
    hero: HTMLElement,
    container: HTMLElement,
    mouseBlob: HTMLElement,

    blueSVG: SVGElement,
    yellowSVG: SVGElement,
    redSVG: SVGElement
  ) {
    this.hero = hero;
    this.container = container;
    this.mouseBlob = mouseBlob;

    this.blueSVG = blueSVG;
    this.yellowSVG = yellowSVG;
    this.redSVG = redSVG;

    this.setPointer = this.setPointer.bind(this);
    this.animate = this.animate.bind(this);
  }

  // Helper to extract the current scale from the transform string

  private getCurrentTransform(el: SVGElement): any {
    const transform = el.style.transform;
    const result = {
      scale: 1,
      translateX: 0,
      translateY: 0,
      rotate: 0,
    };

    if (!transform) return result;

    // Match scale
    const scaleMatch = /scale\(([^)]+)\)/.exec(transform);
    if (scaleMatch) result.scale = parseFloat(scaleMatch[1]);

    // Match translateX
    const txMatch = /translateX\(([^)]+)px\)/.exec(transform);
    if (txMatch) result.translateX = parseFloat(txMatch[1]);

    // Match translateY
    const tyMatch = /translateY\(([^)]+)px\)/.exec(transform);
    if (tyMatch) result.translateY = parseFloat(tyMatch[1]);

    // Match rotate
    const rotMatch = /rotate\(([^)]+)deg\)/.exec(transform);
    if (rotMatch) result.rotate = parseFloat(rotMatch[1]);

    return result;
  }

  // distance in pixels from hero bottom to a given y
  private distanceFromBottom(y: number) {
    const rect = this.hero.getBoundingClientRect();
    return Math.max(0, rect.bottom - y);
  }

  // inverse normalized distance: 1 when near bottom, 0 when far (clamped)
  private distanceToBottom(y: number, maxDistance = 700) {
    const d = this.distanceFromBottom(y);
    const t = Math.min(d / maxDistance, 1);
    return 1 - t;
  }

  public init() {
    const EPSILON = 0.001;

    this.heading = document.getElementById("articleHeroHeading");

    // --- ScrollTrigger sticky and fade ---
    gsap.set(this.hero, { position: "relative", zIndex: 10 });

    // Start mouse tracking immediately on init
    document.addEventListener("mousemove", this.setPointer);
    document.addEventListener("touchmove", this.setPointer, { passive: false });
    this.rafId = requestAnimationFrame(this.animate);

    ScrollTrigger.create({
      trigger: this.hero,
      start: "top top",
      end: "bottom top",
      scrub: true,
      onUpdate: (self) => {
        if (self.progress > EPSILON) {
          document.removeEventListener("mousemove", this.setPointer);
          document.removeEventListener("touchmove", this.setPointer);
          if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
          }
        } else {
          document.addEventListener("mousemove", this.setPointer);
          document.addEventListener("touchmove", this.setPointer, { passive: false });
          if (!this.rafId) {
            this.rafId = requestAnimationFrame(this.animate);
          }
        }

        if (this.heading) {
          gsap.to(this.heading, {
            color: gsap.utils.interpolate("#1a1a1a", "#ffffff", Math.max(0, Math.min((self.progress - 0.3) * 4, 1))),
            duration: 0.2,
            overwrite: "auto",
          });
        }

        const blobProgress = Math.min(self.progress * 3, 1);

        gsap.to(this.yellowSVG, {
          opacity: 1 - blobProgress * 0.8,
          scale: 1 + blobProgress * 2,
          filter: `blur(${30 + blobProgress * 20}px)`,
          duration: 0.4,
          ease: "power1.out",
        });

        gsap.to(this.redSVG, {
          opacity: 1 - blobProgress,
          scale: 1 - blobProgress,
          filter: `blur(${30 + blobProgress * 50}px)`,
          duration: 0.4,
          ease: "power1.out",
        });

        // Get the current scale of blueSVG
        const tf = this.getCurrentTransform(this.blueSVG);
        // Animate from current scale to target scale
        const targetScale = tf.scale + (10 - tf.scale) * self.progress;

        //console.log(
        //  "Blue SVG current scale:",
        //  tf.scale,
        //  "Target scale:",
        //  targetScale
        //);
        //console.log("This blue svg", this.blueSVG);
        gsap.to(this.blueSVG, {
          scale: targetScale,
          x: tf.translateX,
          bottom: 0,
          filter: `blur(30px)`,
          duration: 0.4,
          ease: "power1.out",
        });

        const bluePath = this.blueSVG.querySelector("path");
        if (bluePath) {
          gsap.to(this.hero, {
            backgroundColor: gsap.utils.interpolate("rgba(7, 143, 224, 0)", "#078FE0", self.progress * 1.65),
            duration: 0.2,
            overwrite: "auto",
          });

          gsap.to(this.mouseBlob, {
            opacity: 1 - self.progress * 1.65,
            duration: 0.2,
            overwrite: "auto",
          });
        }
      },
      onLeave: () => {
        //gsap.set(this.letterSpans, { opacity: 0 });
      },
      onEnterBack: () => {
        //gsap.set(this.container, { opacity: 1 });
      },
    });
  }

  private setPointer(e: MouseEvent | TouchEvent) {
    if ("touches" in e && e.touches.length) {
      this.mouseX = e.touches[0].clientX;
      this.mouseY = e.touches[0].clientY;
    } else {
      this.mouseX = (e as MouseEvent).clientX;
      this.mouseY = (e as MouseEvent).clientY;
    }
  }

  private animate() {
    const rect = this.container.getBoundingClientRect();
    const baseY = rect.bottom;

    let x = Math.max(rect.left, Math.min(this.mouseX, rect.right));
    let y = this.mouseY;

    let distFromBottom = this.distanceFromBottom(y);
    let t = Math.min(distFromBottom / 700, 1);

    let scale = 1 + t * 2;

    const offsetX = x - (rect.left + rect.width / 2);

    const blueBlur = 20 + t * 30;

    // Yellow: prominent when near bottom (t=1), more subtle when far/top (t=0)
    const yellowScale = 0.02 + t * 0.58; // 0.02 at top (tiny), 0.6 at bottom
    const yellowOpacity = 0.01 + t * 0.99; // 0.01 at top (barely visible), 1.0 at bottom
    const yellowBlur = 70 - t * 30; // more blur at top

    // Red: subtle hint when near bottom (t=1), more prominent when far/top (t=0)
    const redScale = 0.6 + (1 - t) * 0.8; // 1.4 at top (bigger), 0.6 at bottom
    const redOpacity = 0.5 + (1 - t) * 0.5; // 1.0 at top (full), 0.5 at bottom (more visible)
    const redBlur = 25 + t * 55; // less blur at top (25px), very blurred at bottom (80px)

    // Blue scale: bigger when mouse near bottom (t=1), smaller when at top (t=0)
    const blueScale = 1 + t * 5; // 1 at top, 6 at bottom

    gsap.to(this.blueSVG, {
      x: offsetX,
      scale: blueScale,
      transformOrigin: "50% 100%",
      filter: `blur(${blueBlur}px)`,
    });
    gsap.to(this.yellowSVG, {
      x: offsetX,
      scaleX: scale * yellowScale,
      scaleY: scale * 0.6,
      opacity: yellowOpacity,
      transformOrigin: "50% 100%",
      filter: `blur(${yellowBlur}px)`,
    });
    gsap.to(this.redSVG, {
      x: offsetX,
      scaleX: scale * redScale,
      scaleY: scale * 0.5,
      opacity: redOpacity,
      transformOrigin: "50% 100%",
      filter: `blur(${redBlur}px)`,
    });

    this.rafId = requestAnimationFrame(this.animate);
  }

  public destroy() {
    document.removeEventListener("mousemove", this.setPointer);
    document.removeEventListener("touchmove", this.setPointer);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }
}

// Only initialize if articleHero exists
function initArticleHero() {
  const heroElement = document.getElementById("articleHero");
  const container = document.getElementById("articleHeroContent");
  const blob = document.getElementById("mouse-blob");
  if (!heroElement || !container || !blob) return;

  const [blueSVG, redSVG, yellowSVG] = Array.from(
    blob.querySelectorAll("svg")
  ) as SVGElement[];
  if (!blueSVG || !redSVG || !yellowSVG) return;

  const articleHero = new ArticleHero(
    heroElement,
    container,
    blob,
    blueSVG,
    yellowSVG,
    redSVG
  );
  articleHero.init();
}

// Auto-init on DOMContentLoaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initArticleHero);
} else {
  initArticleHero();
}
